/**
 * Hosts & assignment (section `hosts`, plan 6.E / D16).
 * Group 1 Staff assignment (mode, then the staff dropdown) · Group 2 Event owner · Group 3 Internal notes.
 *
 * Staff accounts are created on the subscription page and listed here. This section only chooses
 * who is on the event.
 *
 * State written here: `availability.staffs` (ids), `availability.assignmentMode`,
 * `availability.staff_assignment_method`, `availability.event_internal_notes` and the per-staff
 * schedule rows (seeded / removed as staff are added / removed — today's component seeds a staff
 * row from the staff's default template when the staff is selected). The payload mapping
 * (`assignmentFields` + `normalizeStaffAssignmentPayload`) is unchanged.
 */

import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { hasPooledAvailability } from "../../../../../utils/organizationStaffAccess";

import { DowngradeNote, FieldError, Helper, PlanBadge, SettingsGroup } from "../../ui/primitives";
import { currentBusinessName, useBuilder } from "../../state/builderContext";
import { ASSIGNMENT_MODE, STAFF_METHOD, STEPS } from "../../state/constants";
import { assignmentMode as modeOf, selectedStaff } from "../../state/selectors";
import { roundRobinAllowed } from "../../state/validation";
import SettingsView from "../shared/SettingsView";
import RichOrPlainText from "../shared/RichOrPlainText";
import useScheduleOps from "../shared/useScheduleOps";

// ---- helpers --------------------------------------------------------------------------------------------------

export const initials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join("") || "?";

const fullName = (s) => `${(s && s.first_name) || ""} ${(s && s.last_name) || ""}`.trim();

const issueFor = (issues, field) => {
  const i = (issues || []).find((x) => x.field === field);
  return i ? i.message : null;
};

const MODES = [
  { id: ASSIGNMENT_MODE.MY, title: "My event", text: "You host every booking" },
  { id: ASSIGNMENT_MODE.ASSIGN, title: "Assign to staff", text: "Select a specific team member" },
  { id: ASSIGNMENT_MODE.INVITEE, title: "Let invitee select staff", text: "Show staff choices on the booking page" },
  { id: ASSIGNMENT_MODE.AUTO, title: "Automatic assignment", text: "MeetOcto selects an available host" },
];

/** Shared by the owner group and the assignment group: every staff edit goes through here. */
const useHostOps = () => {
  const { state, update } = useBuilder();
  const ops = useScheduleOps();
  const a = state.availability;
  const staffs = useMemo(() => (a.staffs || []).map((n) => parseInt(n, 10)).filter((n) => !Number.isNaN(n)), [a.staffs]);
  const mode = modeOf(state);

  /** Invitee / automatic list: membership only. The mode radios decide the assignment mode. */
  const setStaffChecked = (id, on) => {
    if (on) {
      if (staffs.includes(id)) return;
      update(STEPS.AVAILABILITY, { staffs: [...staffs, id] });
      ops.seedStaffRow(id);
      return;
    }
    const next = staffs.filter((x) => x !== id);
    update(STEPS.AVAILABILITY, { staffs: next });
    ops.removeStaffRow(id);
  };

  const removeStaff = (id) => {
    const next = staffs.filter((x) => x !== id);
    const patch = { staffs: next };
    if (!next.length) patch.assignmentMode = ASSIGNMENT_MODE.MY;
    update(STEPS.AVAILABILITY, patch);
    ops.removeStaffRow(id);
  };

  /** "Assign to staff" select: exactly one staff. */
  const chooseSingle = (id) => {
    if (!id) return;
    staffs.filter((x) => x !== id).forEach((x) => ops.removeStaffRow(x));
    update(STEPS.AVAILABILITY, { staffs: [id], assignmentMode: ASSIGNMENT_MODE.ASSIGN });
    ops.seedStaffRow(id);
  };

  const setMode = (m) => {
    const patch = { assignmentMode: m };
    if (m === ASSIGNMENT_MODE.ASSIGN && staffs.length > 1) {
      // single-staff shape: keep the first, drop the others (and their hours rows)
      patch.staffs = staffs.slice(0, 1);
      staffs.slice(1).forEach((x) => ops.removeStaffRow(x));
    }
    if (m === ASSIGNMENT_MODE.MY) {
      // owner-only: staff and their rows are not stored (assignmentFields sends staffs: [])
      patch.staffs = [];
      staffs.forEach((x) => ops.removeStaffRow(x));
    }
    update(STEPS.AVAILABILITY, patch);
  };

  const setMethod = (method) => update(STEPS.AVAILABILITY, { staff_assignment_method: method });

  return { staffs, mode, setStaffChecked, removeStaff, chooseSingle, setMode, setMethod };
};

// ---- Staff list (subscription staff; used under assignment) ----------------------------------------------------------

const StaffPicker = ({ staffs, chosen, activeStaff, onToggle }) => {
  const inactiveChosen = chosen.filter((s) => s.inactive);
  return (
    <div className="eb-large-choice-list" id="staff-picker">
      {activeStaff.map((s) => {
        const id = parseInt(s.id, 10);
        const checked = staffs.includes(id);
        const name = fullName(s);
        return (
          <label key={id} className={checked ? "selected" : ""}>
            <input type="checkbox" checked={checked} onChange={(e) => onToggle(id, e.target.checked)} aria-label={name} />
            <span className="eb-choice-icon">{initials(name)}</span>
            <span>
              <strong>{name}</strong>
              <small>{s.email || ""}</small>
            </span>
            {checked ? <em>Selected</em> : <span />}
          </label>
        );
      })}
      {inactiveChosen.map((s) => (
        <label key={`inactive-${s.id}`} className="selected is-inactive">
          <input type="checkbox" checked onChange={() => onToggle(s.id, false)} aria-label={s.name} />
          <span className="eb-choice-icon">{initials(s.name)}</span>
          <span>
            <strong>{s.name}</strong>
            <small>{s.email || "Inactive"}</small>
          </span>
          <em>Inactive</em>
        </label>
      ))}
      {!activeStaff.length && !inactiveChosen.length ? <Helper>No active staff found. Add staff from the subscription page.</Helper> : null}
    </div>
  );
};

const OwnerGroup = () => {
  const { state, redux } = useBuilder();
  const { staffs, removeStaff } = useHostOps();
  const user = useSelector((s) => (s.auth || {}).user) || {};
  const chosen = selectedStaff(state);
  const ownerName = fullName(user) || currentBusinessName() || "Event owner";

  return (
    <SettingsGroup
      id="hosts-owner"
      title="Event owner"
      text="The owner's calendar and conferencing account are used by default."
    >
      <div className="eb-host-list">
        <div className="eb-host-card">
          <span>{initials(ownerName)}</span>
          <div>
            <strong>{ownerName}</strong>
            <small>
              {user.email ? `${user.email} \u00B7 ` : ""}Event owner
            </small>
          </div>
          <span className="eb-status-pill">Host</span>
        </div>
        {redux.canUseStaff
          ? chosen.map((s) => (
              <div className="eb-host-card secondary" key={s.id}>
                <span>{initials(s.name)}</span>
                <div>
                  <strong>{s.name}</strong>
                  <small>
                    {s.email ? `${s.email} \u00B7 ` : ""}
                    {s.inactive ? "Staff (inactive)" : "Staff"}
                  </small>
                </div>
                <button type="button" className="eb-text-danger text-danger" onClick={() => removeStaff(s.id)}>
                  Remove
                </button>
              </div>
            ))
          : null}
      </div>

      {!redux.canUseStaff ? (
        <>
          <Helper>
            <PlanBadge kind="teams" /> Staff assignment is available on team plans.
          </Helper>
          {staffs.length ? <DowngradeNote>Staff assignment is not in your plan &mdash; this event is saved as your own event the next time this section is saved.</DowngradeNote> : null}
        </>
      ) : null}
    </SettingsGroup>
  );
};

// ---- Staff assignment (rendered above the event owner) -------------------------------------------------------------

const AssignmentGroup = () => {
  const { state, issues, redux } = useBuilder();
  const { staffs, mode, setStaffChecked, chooseSingle, setMode, setMethod } = useHostOps();
  const a = state.availability;
  const hasPooled = hasPooledAvailability(redux.features);
  const chosen = selectedStaff(state);
  const activeStaff = state.lookups.staff;
  const staffsError = issueFor(issues, "staffs");
  const methodError = issueFor(issues, "staff_assignment_method");
  const rrAllowed = roundRobinAllowed(STAFF_METHOD.ROUND_ROBIN, { hadRoundRobin: state.lookups.hadRoundRobin, limits: redux.limits });

  // pooled availability lost after the event was set to automatic: the payload sends "invitee" (staff kept)
  const pooledLost = mode === ASSIGNMENT_MODE.AUTO && !hasPooled;
  const shownMode = pooledLost ? ASSIGNMENT_MODE.INVITEE : mode;

  // "Assign to staff" select: chosen staff first, then the rest of the active list
  const singleOptions = (() => {
    const seen = new Set();
    const out = [];
    chosen.forEach((s) => {
      seen.add(s.id);
      out.push({ id: s.id, name: s.name + (s.inactive ? " (inactive)" : "") });
    });
    (activeStaff || []).forEach((s) => {
      const id = parseInt(s.id, 10);
      if (!seen.has(id)) out.push({ id, name: fullName(s) });
    });
    return out;
  })();

  return (
    <SettingsGroup id="assignment-setting" title="Staff assignment" text="Decide who receives each booking.">
      <div className="eb-assignment-list" role="radiogroup" aria-label="Staff assignment">
        {MODES.map((m) => {
          const locked = m.id === ASSIGNMENT_MODE.AUTO && !hasPooled;
          const selected = shownMode === m.id;
          return (
            <label key={m.id} className={`${selected ? "selected" : ""} ${locked ? "muted-option" : ""}`.trim()}>
              <input type="radio" name="eb-assignment" checked={selected} disabled={locked} onChange={() => !locked && setMode(m.id)} />
              <span>
                <strong>
                  {m.title} {locked ? <PlanBadge kind="upgrade" /> : null}
                </strong>
                <small>{m.text}</small>
              </span>
            </label>
          );
        })}
      </div>

      {pooledLost ? <DowngradeNote>Automatic assignment is not in your plan &mdash; this event is saved as &ldquo;Let invitee select staff&rdquo; the next time this section is saved.</DowngradeNote> : null}

      {shownMode === ASSIGNMENT_MODE.ASSIGN ? (
        <label className="eb-wide-field select-field" id="assign-staff-setting">
          Choose staff member
          <select value={staffs.length ? String(staffs[0]) : ""} onChange={(e) => chooseSingle(parseInt(e.target.value, 10))}>
            <option value="">Select a staff member</option>
            {singleOptions.map((o) => (
              <option key={o.id} value={String(o.id)}>
                {o.name}
              </option>
            ))}
          </select>
          <FieldError message={staffsError} />
        </label>
      ) : null}

      {(shownMode === ASSIGNMENT_MODE.INVITEE || shownMode === ASSIGNMENT_MODE.AUTO) ? (
        <div className="eb-wide-field" id="assign-staff-setting">
          Choose staff members
          <StaffPicker staffs={staffs} chosen={chosen} activeStaff={activeStaff || []} onToggle={setStaffChecked} />
          <FieldError message={staffsError} />
        </div>
      ) : null}

      {shownMode === ASSIGNMENT_MODE.AUTO ? (
        <>
          <div className="eb-radio-panels compact-panels" role="radiogroup" aria-label="Rotation">
            <label className={`${a.staff_assignment_method === STAFF_METHOD.ROUND_ROBIN ? "selected" : ""} ${!rrAllowed ? "muted-option" : ""}`.trim()}>
              <input
                type="radio"
                name="eb-rotation"
                checked={a.staff_assignment_method === STAFF_METHOD.ROUND_ROBIN}
                disabled={!rrAllowed}
                onChange={() => rrAllowed && setMethod(STAFF_METHOD.ROUND_ROBIN)}
              />
              <span>
                <strong>Round-robin</strong>
                <small>Balance bookings evenly.</small>
              </span>
            </label>
            <label className={a.staff_assignment_method === STAFF_METHOD.EVENT_ROTATION ? "selected" : ""}>
              <input
                type="radio"
                name="eb-rotation"
                checked={a.staff_assignment_method === STAFF_METHOD.EVENT_ROTATION}
                onChange={() => setMethod(STAFF_METHOD.EVENT_ROTATION)}
              />
              <span>
                <strong>Event rotation</strong>
                <small>Use your configured sequence.</small>
              </span>
            </label>
          </div>
          {!rrAllowed ? <Helper>You have reached the maximum number of round robin events allowed on your current plan.</Helper> : null}
        </>
      ) : null}

      <FieldError message={methodError} />
    </SettingsGroup>
  );
};

// ---- Group 3 — Notes ------------------------------------------------------------------------------------------------

const NotesGroup = () => {
  const { state, update } = useBuilder();
  return (
    <div className="eb-settings-group" id="notes-setting">
      <label className="eb-wide-field" htmlFor="eb-internal-notes">
        Event internal notes
        <RichOrPlainText
          id="eb-internal-notes"
          value={state.availability.event_internal_notes}
          placeholder="Visible only to you and assigned staff."
          onChange={(v) => update(STEPS.AVAILABILITY, { event_internal_notes: v })}
        />
      </label>
    </div>
  );
};

// ---- Section ------------------------------------------------------------------------------------------------------------

const HostsSection = () => {
  const { redux } = useBuilder();
  return (
    <SettingsView icon="users" title="Hosts & assignment" text="Choose who hosts this event and how bookings are assigned.">
      {redux.canUseStaff ? <AssignmentGroup /> : null}
      <OwnerGroup />
      <NotesGroup />
    </SettingsView>
  );
};

export default HostsSection;
